import React, { useState } from 'react';
import { TrendingUp, DollarSign, Clock, BarChart3, Layers } from 'lucide-react';
import { PriceData } from '../hooks/useOracleAPI';
import { useTokenMetadata } from '../hooks/useTokenMetadata';
import { TokenPoolDetails } from './TokenPoolDetails';

interface PriceGridProps {
  prices: PriceData[];
  loading: boolean;
}

export const PriceGrid: React.FC<PriceGridProps> = ({ prices, loading }) => {
  const [sortBy, setSortBy] = useState<'symbol' | 'price' | 'confidence'>('symbol');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [selectedTokenForPools, setSelectedTokenForPools] = useState<string | null>(null);
  const { getTokenLogo, getTokenName } = useTokenMetadata();

  // Ensure prices is always an array
  const pricesArray = Array.isArray(prices) ? prices : [];

  const filteredPrices = pricesArray
    .filter(p => p.symbol.toLowerCase().includes(filterSymbol.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return b.price - a.price;
        case 'confidence':
          return b.confidence - a.confidence;
        default:
          return a.symbol.localeCompare(b.symbol);
      }
    });

  const formatPrice = (price: number) => {
    if (price < 0.001) return price.toFixed(8);  // Show 8 decimal places for very small numbers
    if (price < 1) return price.toFixed(6);
    if (price < 100) return price.toFixed(4);
    return price.toFixed(2);
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.7) return 'text-green-600 bg-green-100';
    if (confidence >= 0.5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-alephium-500" />
            Token Prices
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-24 bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-alephium-500" />
            Token Prices ({filteredPrices.length})
          </h2>
          
          <div className="flex gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Filter by symbol..."
              value={filterSymbol}
              onChange={(e) => setFilterSymbol(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            />
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              title="Sort prices by"
            >
              <option value="symbol">Sort by Symbol</option>
              <option value="price">Sort by Price</option>
              <option value="confidence">Sort by Confidence</option>
            </select>
          </div>
        </div>
      </div>

      {filteredPrices.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No prices available
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredPrices.map((price) => {
            const tokenLogo = getTokenLogo(price.symbol);
            const tokenName = getTokenName(price.symbol);
            
            return (
              <div key={price.symbol} className="price-card">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {tokenLogo ? (
                      <img 
                        src={tokenLogo} 
                        alt={`${price.symbol} logo`}
                        className="w-8 h-8 rounded-full"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-alephium-500 to-alephium-600 flex items-center justify-center text-white text-xs font-bold">
                        {price.symbol.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{price.symbol}</h3>
                      <p className="text-sm text-gray-500">{tokenName !== price.symbol ? tokenName : price.source}</p>
                    </div>
                  </div>
                  <span className={`status-badge ${getConfidenceColor(price.confidence)}`}>
                    {(price.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-xl font-bold text-gray-900">
                    {formatPrice(price.price)}
                  </span>
                  <span className="text-sm text-gray-500">ALPH</span>
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTimestamp(price.timestamp)}
                  </div>
                  <div className="flex items-center gap-1">
                    <BarChart3 className="w-3 h-3" />
                    {(price.confidence * 100).toFixed(0)}%
                  </div>
                </div>

                <button
                  onClick={() => setSelectedTokenForPools(price.symbol)}
                  className="w-full mt-2 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs py-1 px-2 rounded flex items-center justify-center gap-1 transition-colors"
                >
                  <Layers className="w-3 h-3" />
                  View Pools
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Pool Details Modal */}
      {selectedTokenForPools && (
        <TokenPoolDetails
          symbol={selectedTokenForPools}
          onClose={() => setSelectedTokenForPools(null)}
        />
      )}
    </div>
  );
};
